import React, { useCallback, useEffect, useRef, useState } from "react";
import { X, Eye, EyeOff, ListChecks, FileText, PlayCircle, Check } from "lucide-react";
import ShareLinkButton from "./ShareLinkButton";
import { useLearner } from "../LearnerContext";
import { getMarks, recordActivity, setMark } from "../learnerApi";

// Các phần dưới đây dùng chung với trang chuyên đề (TopicDetailPage)
export const LEVELS = {
  easy: { label: "Dễ", className: "bg-blue-100 text-blue-700" },
  medium: { label: "Trung bình", className: "bg-yellow-100 text-yellow-700" },
  hard: { label: "Khó", className: "bg-red-100 text-red-700" },
};

// Gom các bài liên tiếp cùng dạng thành một nhóm, giữ nguyên thứ tự đã lưu
// (dữ liệu được sắp sẵn: dạng dễ trước, trong mỗi dạng từ dễ đến khó)
export const groupByType = (exercises) =>
  exercises.reduce((groups, ex, index) => {
    const last = groups[groups.length - 1];
    const item = { ...ex, number: index + 1 };
    if (last && last.type === ex.exerciseType) last.items.push(item);
    else groups.push({ type: ex.exerciseType, items: [item] });
    return groups;
  }, []);

export const typeset = (element) => {
  if (element && window.MathJax?.typesetPromise) {
    window.MathJax.typesetPromise([element]).catch((err) =>
      console.warn("MathJax typeset error:", err),
    );
  }
};

// Có nội dung thật (không chỉ là thẻ HTML rỗng)
export const hasText = (html) =>
  Boolean((html || "").replace(/<[^>]*>/g, "").trim());

// Đánh dấu "Làm đúng / Làm sai" của học sinh cho các bài có mã bắt đầu bằng prefix
// (ví dụ "topic:003:"). Chỉ tải khi máy đã mở khoá học viên.
export const useExerciseMarks = (prefix) => {
  const { learner } = useLearner();
  const [marks, setMarks] = useState({});

  useEffect(() => {
    let cancelled = false;
    if (learner && prefix) {
      getMarks(prefix).then((data) => !cancelled && setMarks(data));
    } else {
      setMarks({});
    }
    return () => {
      cancelled = true;
    };
  }, [learner, prefix]);

  const mark = useCallback(async (refKey, result, title) => {
    setMarks((prev) => {
      const next = { ...prev };
      if (result) next[refKey] = result;
      else delete next[refKey];
      return next;
    });
    await setMark(refKey, result, title);
  }, []);

  return { enabled: Boolean(learner && prefix), marks, mark };
};

// Một bài tập. tracking = { refKey, title, marks } để ghi việc xem lời giải và cho học sinh tự đánh dấu
// đã làm đúng / sai (marks là kết quả của useExerciseMarks).
export const ExerciseCard = ({ exercise, tracking }) => {
  const [showSolution, setShowSolution] = useState(false);
  const solutionRef = useRef(null);
  const level = LEVELS[exercise.level];
  const hasSolution = hasText(exercise.solution);
  const refKey = tracking ? `${tracking.refKey}${exercise.id || exercise._id}` : null;
  const result = refKey ? tracking.marks?.marks[refKey] : null;
  const label = `Bài ${exercise.number}${exercise.title ? ` (${exercise.title})` : ""}`;

  useEffect(() => {
    if (showSolution) typeset(solutionRef.current);
  }, [showSolution]);

  const toggleSolution = () => {
    if (!showSolution && tracking) {
      recordActivity({ type: "view_solution", refKey, title: tracking.title, detail: label });
    }
    setShowSolution((v) => !v);
  };

  const markButton = (value, text, activeClass) => (
    <button
      type="button"
      onClick={() => tracking.marks.mark(refKey, result === value ? null : value, `${tracking.title} › ${label}`)}
      className={`flex items-center text-xs font-semibold border rounded-full px-2.5 py-1 ${
        result === value ? activeClass : "bg-white text-gray-600 border-gray-300"
      }`}
    >
      {value === "correct" ? <Check size={13} className="mr-1" /> : <X size={13} className="mr-1" />}
      {text}
    </button>
  );

  return (
    <div className="border rounded-lg p-3 md:p-4 mb-3 bg-white shadow-sm">
      <div className="flex items-center justify-between mb-2 gap-2">
        <div className="flex items-center gap-2">
          <span className="font-bold text-gray-800">Bài {exercise.number}</span>
          {level && (
            <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${level.className}`}>
              {level.label}
            </span>
          )}
          {result && (
            <span
              className={`text-xs font-semibold ${result === "correct" ? "text-green-600" : "text-red-600"}`}
            >
              {result === "correct" ? "✓ Đã làm đúng" : "✗ Đã làm sai"}
            </span>
          )}
        </div>
        {hasSolution && (
          <button
            onClick={toggleSolution}
            className="flex items-center px-3 py-1 bg-indigo-500 text-white rounded-full hover:bg-indigo-600 transition-colors shadow text-xs flex-shrink-0"
          >
            {showSolution ? <EyeOff size={14} className="mr-1" /> : <Eye size={14} className="mr-1" />}
            {showSolution ? "Ẩn lời giải" : "Xem lời giải"}
          </button>
        )}
      </div>
      <div className="text-gray-700 exercise-html" dangerouslySetInnerHTML={{ __html: exercise.content }} />
      {showSolution && (
        <div
          ref={solutionRef}
          className="mt-3 border-t pt-3 text-gray-800 bg-green-50 -mx-3 md:-mx-4 px-3 md:px-4 pb-3 md:pb-4"
        >
          <strong className="text-green-700">Lời giải:</strong>
          <div className="mt-1 exercise-html" dangerouslySetInnerHTML={{ __html: exercise.solution }} />
        </div>
      )}
      {tracking?.marks?.enabled && (
        <div className="flex flex-wrap items-center gap-2 mt-3 pt-2 border-t text-sm text-gray-600">
          <span>Em làm bài này:</span>
          {markButton("correct", "Làm đúng", "bg-green-600 text-white border-green-600")}
          {markButton("wrong", "Làm sai", "bg-red-600 text-white border-red-600")}
        </div>
      )}
    </div>
  );
};

// Định dạng nội dung HTML của tài liệu và bài tập
export const CONTENT_STYLES = `
        .exercise-html p { margin-bottom: 0.4rem; }
        .exercise-html ul { list-style: disc; padding-left: 1.25rem; margin-bottom: 0.4rem; }
        .exercise-html ol { list-style: decimal; padding-left: 1.25rem; margin-bottom: 0.4rem; }
        .exercise-html svg { max-width: 100%; height: auto; }
        .exercise-html mjx-container { overflow-x: auto; overflow-y: hidden; max-width: 100%; }
        .lesson-doc h3 { font-size: 1.1rem; font-weight: 700; color: #4338ca; margin: 1.25rem 0 0.5rem; }
        .lesson-doc h3:first-child { margin-top: 0; }
        .lesson-doc .doc-box, .lesson-doc .doc-note, .lesson-doc .doc-remember, .lesson-doc .doc-example {
          border-radius: 0.5rem; padding: 0.75rem 1rem; margin: 0.75rem 0;
        }
        .lesson-doc .doc-box { background: #eef2ff; border-left: 4px solid #6366f1; }
        .lesson-doc .doc-note { background: #fffbeb; border-left: 4px solid #f59e0b; }
        .lesson-doc .doc-remember { background: #ecfdf5; border-left: 4px solid #10b981; }
        .lesson-doc .doc-example { background: #fff; border: 1px solid #e5e7eb; }
        .lesson-doc details { margin-top: 0.5rem; }
        .lesson-doc summary {
          display: inline-block; cursor: pointer; font-size: 0.8rem; font-weight: 600;
          color: #fff; background: #6366f1; border-radius: 9999px; padding: 0.2rem 0.75rem;
          list-style: none;
        }
        .lesson-doc summary::-webkit-details-marker { display: none; }
        .lesson-doc details[open] summary { background: #9ca3af; margin-bottom: 0.5rem; }
        .lesson-doc details[open] > :not(summary) { background: #f0fdf4; padding: 0.25rem 0.75rem; margin: 0; }
`;

// Popup nội dung của một mục trong khoá học:
// mode "document" hiện tài liệu (lý thuyết, ví dụ), mode "exercises" hiện danh sách bài tập kèm lời giải
const LessonContentModal = ({
  lesson,
  topicTitle,
  mode,
  shareUrl,
  onOpenVideo,
  onClose,
  // { refKey: tiền tố mã bài, ví dụ "course:1:lesson123:", title: "Toán 6 › Bài 1 › Bài tập ôn luyện" }
  // để ghi việc xem lời giải và cho học sinh đánh dấu đã làm đúng / sai
  tracking,
}) => {
  const bodyRef = useRef(null);
  const marks = useExerciseMarks(lesson && mode === "exercises" ? tracking?.refKey : null);

  useEffect(() => {
    if (!lesson) return;
    if (bodyRef.current) bodyRef.current.scrollTop = 0;
    typeset(bodyRef.current);
  }, [lesson, mode]);

  if (!lesson) return null;
  const isDocument = mode === "document";
  const groups = isDocument ? [] : groupByType(lesson.exercises || []);
  const Icon = isDocument ? FileText : ListChecks;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 md:p-4"
      onClick={onClose}
    >
      <div
        className="bg-gray-50 w-full h-full md:h-auto md:max-h-[90vh] md:max-w-3xl md:rounded-xl shadow-xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between p-4 border-b bg-white md:rounded-t-xl">
          <div>
            {topicTitle && (
              <p className="text-sm text-gray-500">{topicTitle}</p>
            )}
            <h3 className="text-lg md:text-xl font-bold text-gray-800 flex items-center">
              <Icon size={22} className="text-indigo-600 mr-2 flex-shrink-0" />
              {lesson.title}
            </h3>
            {!isDocument && (
              <p className="text-xs text-gray-500 mt-1">
                {lesson.exercises.length} bài · {groups.length} dạng · sắp xếp
                từ dễ đến khó
              </p>
            )}
            {isDocument && lesson.videoUrl && onOpenVideo && (
              <button
                onClick={onOpenVideo}
                className="mt-2 flex items-center px-3 py-1 bg-indigo-500 text-white rounded-full hover:bg-indigo-600 shadow text-xs"
              >
                <PlayCircle size={14} className="mr-1" /> Xem video bài giảng
              </button>
            )}
          </div>
          <div className="flex items-center flex-shrink-0">
            {shareUrl && (
              <ShareLinkButton
                url={shareUrl}
                title={`${topicTitle} - ${lesson.title}`}
                className="p-2"
              />
            )}
            <button
              onClick={onClose}
              className="p-2 rounded-full hover:bg-gray-100 text-gray-600"
              aria-label="Đóng"
            >
              <X size={22} />
            </button>
          </div>
        </div>
        <div ref={bodyRef} className="overflow-y-auto p-3 md:p-5">
          {isDocument ? (
            <article
              className="lesson-doc exercise-html bg-white border rounded-lg p-4 md:p-6 text-gray-800"
              dangerouslySetInnerHTML={{ __html: lesson.document }}
            />
          ) : (
            <>
              {/* Đề cương ôn tập: phần nội dung cần ôn trước danh sách bài tập */}
              {lesson.intro && (
                <article
                  className="lesson-doc exercise-html bg-white border rounded-lg p-4 md:p-6 mb-5 text-gray-800"
                  dangerouslySetInnerHTML={{ __html: lesson.intro }}
                />
              )}
              {groups.map((group, i) => (
                <section key={i} className="mb-5">
                  <h4 className="font-bold text-indigo-700 mb-2">
                    Dạng {i + 1}: {group.type}
                  </h4>
                  {group.items.map((ex) => (
                    <ExerciseCard
                      key={ex.id}
                      exercise={ex}
                      tracking={tracking && { ...tracking, marks }}
                    />
                  ))}
                </section>
              ))}
            </>
          )}
        </div>
      </div>
      <style>{CONTENT_STYLES}</style>
    </div>
  );
};

export default LessonContentModal;
